unit uStrLists;
(*
  We've been running into problems between stringlists that have different separators,
  delimiters, and similar subtle issues, so I thought it would make sense to create
  some subclasses that clearly represent what they are for. These problems arise when
  moving data between JSON things and Stringlists.

  TCSVList is just a plain old TStringlist that contains a list of strings separated by commas
  TEqlStrList is for <name> = <value> pairs
  TColonStrList is for <name> : <value> pairs
  TCaretStrList is for <name> ^ <value> pairs
  TJAStrList is for a JSON Array of TColonStrLists: <name> : [ <TCSVList> ]

  any <value> can be represented by any of these simply by setting the <value> to '<<obj: [type_nm] >>' and that
  means to look at the Objects array for this item's ItemIndex and the given type is one of the above.
  For example: available_formats : <<obj:TJAStrList>> and its Objects member is a TJAStrList like:
    available_formats : [ "txt", "markdown" ]  where the quotes may be optional


  Some TStringlist properties from the help files:


  NameValueSeparator
  ------------------
  Indicates the character used to separate names from values.

  Strings that contain the NameValueSeparator character are considered name-value pairs.
  NameValueSeparator defaults to the equal sign (=). TStrings defines various methods for accessing
  names and values and for searching for specific names.

  Strings that are name-value pairs consist of a name part, the separator character, and a value part.
  Any spaces around the separator character are part of the name or value. This convention corresponds
  to the format used in many initialization files.

  Related properties include:

      * ValueFromIndex
      * Names
      * IndexOfName
      * Strings
      * Values


  DelimitedText
  -------------
  Represents all the strings in the TStrings object as a single delimited string.

  Use DelimitedText to get or set all the strings in the TStrings object in a single string, separated
  by the character specified by the Delimiter property.

  When retrieving DelimitedText, any string in the list that includes spaces or the delimiter and
  quotation marks characters specified in the Delimiter and QuoteChar properties will be surrounded
  (before and after) by the quotation mark character (QuoteChar). In addition, any QuoteChar character
  contained in an individual string will be repeated.

  When retrieving DelimitedText, the resulting value delimits individual strings in two ways: each
  string is surrounded (before and after) by the quotation marks character specified by the QuoteChar
  property. In addition, individual strings are separated by the character specified by the Delimiter
  property.

  When assigning DelimitedText, individual strings must be separated using Delimiter marks, and
  optionally enclosed in QuoteChar characters.

  When assigning DelimitedText, the value is parsed as SDF formatted text. For SDF format, strings are
  separated by Delimiter characters or spaces, and optionally enclosed in QuoteChar characters.
  QuoteChar marks that are part of the string are repeated to distinguish them from the QuoteChar
  characters that surround the string. Spaces and Delimiter characters that are not contained within
  QuoteChar marks are delimiters. Two Delimiter characters next to each other will indicate an empty
  string, but spaces that appear next to another Delimiter character are ignored.

  If StrictDelimiter is set to False, the space character is also interpreted as a delimiter,
  regardless of the value of Delimiter. This is not true when the space character occurs between
  quotation marks.

  Note: CommaText is the same as the DelimitedText property when Delimiter is ',' and QuoteChar
  is '"'. Including a trailing Delimiter in the source string causes a blank item to be included in
  the string list.

  Related properties include:

      * CommaText
      * Delimiter
      * Text
      * QuoteChar
      * StrictDelimiter

*)

interface

uses
  System.SysUtils, System.Classes, System.StrUtils, WEBLib.JSON, JS, Web;

type

  // this is a common base class
  // the AsJSONxxx methods can be implemented only if they make sense
  TCustomStringlist = class(TStringlist)
  private
    function  GetAsJSONArr: TJSONArray;  virtual;
    function  GetAsJSONObj: TJSONObject; virtual;
    function  GetAsJSONVal: TJSONValue;  virtual;
    procedure SetAsJSONArr(const Value: TJSONArray);  virtual;
    procedure SetAsJSONObj(const Value: TJSONObject); virtual;
    procedure SetAsJSONVal(const Value: TJSONValue);  virtual;
    function  GetNameValueSeparator: char;
    procedure SetNameValueSeparator(const Value: char);

  public
    constructor Create( aNSV : Char ); virtual; // required to get inherited create to work
    procedure Assign(Source: TCustomStringlist); reintroduce; virtual;

    property AsJSONVal : TJSONValue  read GetAsJSONVal write SetAsJSONVal;
    property AsJSONObj : TJSONObject read GetAsJSONObj write SetAsJSONObj;
    property AsJSONArr : TJSONArray  read GetAsJSONArr write SetAsJSONArr;
    property NameValueSeparator : char read GetNameValueSeparator write SetNameValueSeparator;
  end;

  // a plain old TStringlist that contains a list of strings separated by commas
  TCSVList = class(TCustomStringlist)
  private
    function  GetAsJSONArr: TJSONArray;  override;
    procedure SetAsJSONArr(const aJArry: TJSONArray);  override;
//    function  GetAsJSONObj: TJSONObject; override;
//    function  GetAsJSONVal: TJSONValue;  override;
//    procedure SetAsJSONObj(const Value: TJSONObject); override;
//    procedure SetAsJSONVal(const Value: TJSONValue);  override;

  public
    constructor Create;
    constructor CreateFromTStrings(aStrs: TStrings);
    constructor CreateFromCommaText(const aCommaTextStr: string);

    procedure Assign(Source: TCustomStringlist); override;
    procedure AssignTStrings(aStrs : TStrings);
  end;

  //  TEqlStrList is for <name> = <value> pairs
  TEqlStrList = class(TCustomStringlist)
  private
{    function  GetAsJSONArr: TJSONArray;  override;
    function  GetAsJSONObj: TJSONObject; override;
    function  GetAsJSONVal: TJSONValue;  override;
    procedure SetAsJSONArr(const Value: TJSONArray);  override;
    procedure SetAsJSONObj(const Value: TJSONObject); override;
    procedure SetAsJSONVal(const Value: TJSONValue);  override; }

  public
    constructor Create;
    procedure Assign(Source: TCustomStringlist); override;
  end;

  //  TColonStrList is for <name> : <value> pairs
  TColonStrList = class(TCustomStringlist)
  private
//    function  GetAsJSONArr: TJSONArray;  override;
//    procedure SetAsJSONArr(const aJArry: TJSONArray);  override;
    function  GetAsJSONObj: TJSONObject; override;
    procedure SetAsJSONObj(const aJObj: TJSONObject); override;
//    function  GetAsJSONVal: TJSONValue;  override;
//    procedure SetAsJSONVal(const Value: TJSONValue);  override;

  public
    constructor Create;
    procedure Assign(Source: TCustomStringlist); override;
  end;

  //  TCaretStrList is for <name> ^ <value> pairs
  TCaretStrList = class(TCustomStringlist)
  private
{    function  GetAsJSONArr: TJSONArray;  override;
    function  GetAsJSONObj: TJSONObject; override;
    function  GetAsJSONVal: TJSONValue;  override;
    procedure SetAsJSONArr(const Value: TJSONArray);  override;
    procedure SetAsJSONObj(const Value: TJSONObject); override;
    procedure SetAsJSONVal(const Value: TJSONValue);  override;  }

  public
    constructor Create;
    procedure Assign(Source: TCustomStringlist); override;
  end;

  //  TJAStrList is for a JSON Array of TColonStrLists: <name> : [ <TCSVList> ]
  TJAStrList = class(TCustomStringlist)
  private
    function  GetAsJSONArr: TJSONArray;  override;
    procedure SetAsJSONArr(const Value: TJSONArray);  override;
//    function  GetAsJSONObj: TJSONObject; override;
//    function  GetAsJSONVal: TJSONValue;  override;
//    procedure SetAsJSONObj(const Value: TJSONObject); override;
//    procedure SetAsJSONVal(const Value: TJSONValue);  override;

  public
    constructor Create;
    constructor CreateFromJSONString(const aJSONStr: string);
    procedure Assign(Source: TCustomStringlist); override;
    procedure AssignTStrings(aStrs : TStrings);
    procedure AssignToTStrings(aStrs : TStrings);
  end;

{
  //  TCSVList is just a plain old TStringlist that contains a list of strings separated by commas
  //  TEqlStrList is for <name> = <value> pairs
  //  TColonStrList is for <name> : <value> pairs
  //  TCaretStrList is for <name> ^ <value> pairs
  //  TJAStrList is for a JSON Array of TColonStrLists: <name> : [ <TCSVList> ]

}

implementation

{ TCustomStringlist }

const
  NOT_YET = ' : Not Yet Implemented Yet';

function  TCustomStringlist.GetAsJSONArr: TJSONArray; begin  raise Exception.Create( 'GetAsJSONArr'+NOT_YET ); end;
function  TCustomStringlist.GetAsJSONObj: TJSONObject; begin raise Exception.Create( 'GetAsJSONObj'+NOT_YET ); end;
function  TCustomStringlist.GetAsJSONVal: TJSONValue; begin  raise Exception.Create( 'GetAsJSONVal'+NOT_YET ); end;
procedure TCustomStringlist.SetAsJSONArr(const Value: TJSONArray);  begin raise Exception.Create( 'SetAsJSONArr'+NOT_YET ); end;
procedure TCustomStringlist.SetAsJSONObj(const Value: TJSONObject); begin raise Exception.Create( 'SetAsJSONObj'+NOT_YET ); end;
procedure TCustomStringlist.SetAsJSONVal(const Value: TJSONValue);  begin raise Exception.Create( 'SetAsJSONVal'+NOT_YET ); end;

//=======================================================
procedure TCustomStringlist.Assign(Source: TCustomStringlist);
//=======================================================
begin
  inherited Assign(Source);
end;

//=======================================================
constructor TCustomStringlist.Create( aNSV : Char );
//=======================================================
begin
  inherited Create;
  inherited NameValueSeparator := aNSV;
end;

//=======================================================
function TCustomStringlist.GetNameValueSeparator: char;
//=======================================================
begin
  Result := inherited NameValueSeparator;
end;

//=======================================================
procedure TCustomStringlist.SetNameValueSeparator(const Value: char);
//=======================================================
begin
  raise Exception.Create( '** TCustomStringlist ERROR: Cannot change NameValueSeparator!' );
end;

{ TCSVList }

//  TCSVList is just a plain old TStringlist that contains a list of strings separated by commas
//=======================================================
constructor TCSVList.Create;
//=======================================================
begin
  inherited Create( '=' );
  StrictDelimiter := True; // keeps us from splitting on spaces, just the DelimiterChar
end;

//=======================================================
constructor TCSVList.CreateFromTStrings( aStrs : TStrings );
//=======================================================
begin
  inherited Create( '=' );
  StrictDelimiter := True; // keeps us from splitting on spaces, just the DelimiterChar
  AssignTStrings( aStrs );
end;

//=======================================================
constructor TCSVList.CreateFromCommaText( const aCommaTextStr: string );
//=======================================================
begin
  inherited Create( '=' );
  StrictDelimiter := True; // keeps us from splitting on spaces, just the DelimiterChar
  CommaText := aCommaTextStr;

console.log('>>> TCSVList.CreateFromCommaText: {'+aCommaTextStr+'}');   // $%^ ###

end;

//=======================================================
procedure TCSVList.AssignTStrings(aStrs : TStrings);
//=======================================================
begin
  if not (aStrs is TStrings) then
    raise Exception.Create( '** TCSVList.AssignTStrings ERROR: wrong type' );
  Clear;
  AddStrings(aStrs);
end;

//=======================================================
procedure TCSVList.Assign(Source: TCustomStringlist);
//=======================================================
begin
  if not (Source is TCSVList) then
    raise Exception.Create( '** TCSVList.Assign ERROR: wrong type' );
  inherited;
end;

//=======================================================
function TCSVList.GetAsJSONArr: TJSONArray;
//=======================================================
var i : integer;
begin
  Result := TJSONArray.Create;
  for i := 0 to Count-1 do
    Result.Add(Strings[i]);
end;

//=======================================================
procedure TCSVList.SetAsJSONArr(const aJArry: TJSONArray);
//=======================================================
var i : integer;
begin
  Clear;
  if not Assigned(aJArry) then Exit;
  for i := 0 to aJArry.Count-1 do
    Add(aJArry.Items[i].Value);
end;

{ TEqlStrList }

//  TEqlStrList is for <name> = <value> pairs
//=======================================================
constructor TEqlStrList.Create;
//=======================================================
begin
  inherited Create( '=' );

end;

//=======================================================
procedure TEqlStrList.Assign(Source: TCustomStringlist);
//=======================================================
begin
  if not (Source is TEqlStrList) then
    raise Exception.Create( '** TEqlStrList.Assign ERROR: wrong type' );
  inherited;
end;

{ TColonStrList }

//  TColonStrList is for <name> : <value> pairs
//=======================================================
constructor TColonStrList.Create;
//=======================================================
begin
  inherited Create( ':' );

end;

//=======================================================
procedure TColonStrList.Assign(Source: TCustomStringlist);
//=======================================================
begin
  if not (Source is TColonStrList) then
    raise Exception.Create( '** TColonStrList.Assign ERROR: wrong type' );
  inherited;
end;

//=======================================================
function TColonStrList.GetAsJSONObj: TJSONObject;
//=======================================================
var
  i   : integer;
  val : string;
  arr : TJSONArray;
begin
  Result := TJSONObject.Create;
  for i := 0 to Count-1 do
  begin
    val := ValueFromIndex[i];
    if StartsStr('[', Trim(val)) then
    begin
      arr := TJSONObject.ParseJSONValue(val) as TJSONArray;
      if Assigned(arr) then
        Result.AddPair(Names[i], arr)
      else
        Result.AddPair(Names[i], TJSONString.Create(val));
    end
    else
      Result.AddPair(Names[i], TJSONString.Create(val));
  end;
end;

//=======================================================
procedure TColonStrList.SetAsJSONObj(const aJObj: TJSONObject);
//=======================================================
var i : integer;
begin
  Clear;
  if not Assigned(aJObj) then Exit;
  for i := 0 to aJObj.Count-1 do
    Values[aJObj.Pairs[i].JsonString.Value] := aJObj.Pairs[i].JsonValue.Value;
end;

{ TCaretStrList }

//  TCaretStrList is for <name> ^ <value> pairs
//=======================================================
constructor TCaretStrList.Create;
//=======================================================
begin
  inherited Create( '^' );

end;

//=======================================================
procedure TCaretStrList.Assign(Source: TCustomStringlist);
//=======================================================
begin
  if not (Source is TCaretStrList) then
    raise Exception.Create( '** TCaretStrList.Assign ERROR: wrong type' );
  inherited;
end;

{ TJAStrList }

//  TJAStrList is for a JSON Array of TColonStrLists: <name> : [ <TCSVList> ]
//=======================================================
constructor TJAStrList.Create;
//=======================================================
begin
  inherited Create( ':' );

end;

//=======================================================
constructor TJAStrList.CreateFromJSONString(const aJSONStr: string);
//=======================================================
begin
  inherited Create(':');
  AsJSONArr := TJSONObject.ParseJSONValue(aJSONStr) as TJSONArray;
end;

//=======================================================
procedure TJAStrList.Assign(Source: TCustomStringlist);
//=======================================================
begin
  if not (Source is TCSVList) then
    raise Exception.Create( '** TJAStrList.Assign ERROR: wrong type' );
  inherited;
end;

//=======================================================
procedure TJAStrList.AssignToTStrings(aStrs: TStrings);
//=======================================================
begin
  if not (aStrs is TStrings) then
    raise Exception.Create( '** TJAStrList.AssignToTStrings ERROR: wrong type' );
  aStrs.Assign(self);
end;

//=======================================================
procedure TJAStrList.AssignTStrings(aStrs: TStrings);
//=======================================================
begin
  if not (aStrs is TStrings) then
    raise Exception.Create( '** TJAStrList.AssignTStrings ERROR: wrong type' );
  Clear;
  AddStrings(aStrs);
end;

//=======================================================
function TJAStrList.GetAsJSONArr: TJSONArray;
//=======================================================
var
  i   : integer;
begin
  Result := TJSONArray.Create;
  for i := 0 to Count-1 do
    if (Trim(Strings[i]) <> '') then
      Result.Add(Trim(Strings[i]));
end;

//=======================================================
procedure TJAStrList.SetAsJSONArr(const Value: TJSONArray);
//=======================================================
var
  i : integer;
begin
  Clear;
  if not Assigned(Value) then Exit;
  for i := 0 to Value.Count-1 do
    Add(Value.Items[i].Value);
end;

end.
